import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Save, Trash2 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const AccountSettingsPage = () => {
  const { user, profile, updateProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    setDisplayName(profile?.name || '');
  }, [profile]);

  const formatDate = (value) => {
    if (!value) return 'N/A';
    return new Date(value).toLocaleString();
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);

    try {
      const { error } = await updateProfile({ name: displayName.trim().slice(0, 50) });
      if (error) throw error;
      toast.success('Profile updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    if (!user || !profile) return;
    setExporting(true);

    try {
      const [filesRes, logsRes] = await Promise.all([
        supabase
          .from('files')
          .select('id,file_name,file_size,created_at,share_expiry')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('logs')
          .select('action,created_at,ip_address')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }),
      ]);

      if (filesRes.error) throw filesRes.error;
      if (logsRes.error) throw logsRes.error;

      const exportData = {
        profile: {
          id: profile.id,
          name: profile.name,
          email: profile.email,
          role: profile.role,
          created_at: profile.created_at,
        },
        files: filesRes.data || [],
        logs: logsRes.data || [],
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'securevault-data-export.json';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success('Data export downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccountData = async () => {
    if (!user) return;
    setDeleting(true);

    try {
      const { data: fileRows, error: fetchFilesError } = await supabase
        .from('files')
        .select('storage_path')
        .eq('user_id', user.id);

      if (fetchFilesError) throw fetchFilesError;

      const storagePaths = (fileRows || [])
        .map((file) => file.storage_path)
        .filter(Boolean);

      if (storagePaths.length > 0) {
        const { error: storageError } = await supabase.storage
          .from('vault-files')
          .remove(storagePaths);

        if (storageError) throw storageError;
      }

      const { error: filesError } = await supabase.from('files').delete().eq('user_id', user.id);
      if (filesError) throw filesError;

      const { error: logsError } = await supabase.from('logs').delete().eq('user_id', user.id);
      if (logsError) throw logsError;

      const { error: eventsError } = await supabase.from('suspicious_events').delete().eq('user_id', user.id);
      if (eventsError) throw eventsError;

      await signOut();
      toast.success('Account data deleted. Contact support to fully remove your auth account.');
      navigate('/login');
    } catch (error) {
      toast.error(error.message || 'Failed to delete account data');
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="account-settings-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Account Settings
          </h1>
          <p className="text-base text-muted-foreground mt-1">Manage your profile, exports, and account data.</p>
        </div>

        <section className="bg-card border border-border rounded-lg p-6 space-y-5">
          <div>
            <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Profile Settings
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Update your display name and review account details.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="display-name">Display Name</Label>
              <Input
                id="display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value.slice(0, 50))}
                maxLength={50}
                className="mt-2"
                data-testid="settings-display-name-input"
              />
            </div>
            <div>
              <Label>Email Address</Label>
              <Input value={profile?.email || user?.email || ''} readOnly className="mt-2 font-mono text-sm" />
            </div>
            <div>
              <Label>Account Created</Label>
              <Input value={formatDate(profile?.created_at)} readOnly className="mt-2 font-mono text-sm" />
            </div>
            <div>
              <Label>Current Role</Label>
              <Input value={profile?.role || 'user'} readOnly className="mt-2 font-mono text-sm capitalize" />
            </div>
          </div>

          <Button onClick={handleSaveProfile} disabled={saving} className="w-full sm:w-auto" data-testid="settings-save-profile-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Profile'}
          </Button>
        </section>

        <section className="bg-card border border-border rounded-lg p-6 space-y-5">
          <div>
            <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Export My Data
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Download a JSON export of your profile, file metadata, and activity logs. Sensitive fields are excluded.
            </p>
          </div>

          <Button onClick={handleExportData} disabled={exporting} variant="outline" className="w-full sm:w-auto" data-testid="settings-export-data-btn">
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export My Data'}
          </Button>
        </section>

        <section className="bg-card border border-destructive/40 rounded-lg p-6 space-y-5">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-destructive" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Danger Zone
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Permanently delete your files, logs, and account-related data stored by SecureVault.
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() => setDeleteOpen(true)}
            className="w-full sm:w-auto text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/40"
            data-testid="settings-delete-account-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete My Account
          </Button>
        </section>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your account, all your files, and all your data. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccountData}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="settings-confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete My Account'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default AccountSettingsPage;
